import { ajax } from "lib/observable";
import querystring from "querystring";
import { normalize } from "normalizr";
import moment from "moment";
import { Observable } from "rxjs";
import { add as snackbarAdd } from "actions/snackbars";
import { formatMessage } from "lib/globalize-util";
import "rxjs/add/operator/map";
import {
    WHITEBOARD_COMPONENT_ADD,
    WHITEBOARD_COMPONENT_ARRANGE,
    WHITEBOARD_COMPONENT_MUTATE,
    WHITEBOARD_COMPONENT_PASTE,
    WHITEBOARD_COMPONENT_REMOVE,
    WHITEBOARD_COMPONENT_REMOVE_COMPLETED,
    WHITEBOARD_COMPONENTS_REFRESH,
} from "constants/action-types";
import { Items as ItemsSchema } from "schemas/items";
import siteConfig from "site-config";
import { mapRemoveOperation, mapSetOperation } from "actions/mutate";
import { components as componentActions, products as productActions } from "actions/whiteboard";
import { componentDefaults } from "constants/whiteboard";
import uuid from "uuid/v4";
import { empty } from "rxjs/observable/empty";

import { getOverlayingComponents, getUnderlyingComponents } from "components/whiteboard/constraints";
import me from "models/me";
import { VIEW_ITEM_TILE } from "constants/view-defs";

const recordCache = {};

const calcuateNewComponentZIndex = (page) => {
    let zIndex = 999;
    page.forEach((value) => {
        const z = parseInt(value.get("position").get("z_index"), 10);
        if (!isNaN(z) && z > zIndex) {
            zIndex = z;
        }
    });
    return zIndex + 1;
};

export const addComponentToPageEpic = (action$, store) => {
    return action$
        .ofType(WHITEBOARD_COMPONENT_ADD)
        .map(({ payload: { type, props, select } }) => {
            const { whiteboard, mutate } = store.getState();
            const { currentPageId } = whiteboard;
            const page = mutate.snapshots.get(currentPageId);

            let productUrl = null;
            if (props.product_image && props.product_image.url) {
                productUrl = props.product_image.url;
                delete props.product_image;
            }

            const component = componentDefaults[type].mergeDeep(props, {
                id: uuid(),
                position: { z_index: calcuateNewComponentZIndex(page) },
            });
            return { component, currentPageId, select, productUrl, whiteboard };
        })
        .flatMap(({ component, currentPageId, select, productUrl, whiteboard }) => {
            const actions = [];

            actions.push(mapSetOperation(currentPageId, component.get("id"), component, { buffer: false }));

            if (productUrl) {
                // We can't just use the product images stored on s3, but have to create a
                // board asset with that url
                actions.push(
                    productActions.changeProductImage({ url: productUrl, position: 1, component, whiteboard }),
                );
            }

            if (select) {
                actions.push(componentActions.current.set(component.get("id")));
            }

            return actions;
        });
};
// TODO: mod so we move to top of board if overflows
const getPositionOfNewComponent = (board, page, newComponent) => {
    const pageSize = board.getIn(["page_size", "struct_value"]);
    const boardWidth = pageSize.get("width");
    const boardHeight = pageSize.get("height");
    let left = newComponent.getIn(["position", "left"]);
    let top = newComponent.getIn(["position", "top"]);
    const positions = page.reduce((positionHashMap, child) => {
        const key = `${child.get("position").get("left")}x${child.get("position").get("top")}`;
        positionHashMap[key] = true;
        return positionHashMap;
    }, {});

    let i = 0;
    while (positions[`${left}x${top}`]) {
        left = (left + 25) % boardWidth;
        top = (top + 25) % boardHeight;
        // Prevent infinite loop since we wrap around
        if (i > 100) {
            break;
        }
        i++;
    }

    return { left, top };
};

export const pasteComponentToPageEpic = (action$, store) => {
    return action$
        .ofType(WHITEBOARD_COMPONENT_PASTE)
        .filter(({ payload }) => {
            const { whiteboard } = store.getState();
            return !!whiteboard.copiedComponent || (payload && !!payload.component);
        })
        .map(({ payload }) => {
            const { whiteboard, mutate } = store.getState();
            const { boardId, currentPageId } = whiteboard;
            const board = mutate.snapshots.get(boardId);
            const page = mutate.snapshots.get(currentPageId);
            const componentToCopy = payload && payload.component ? payload.component : whiteboard.copiedComponent;
            const component = componentToCopy.mergeDeep({
                id: uuid(),
                position: {
                    ...getPositionOfNewComponent(board, page, componentToCopy),
                    z_index: calcuateNewComponentZIndex(page),
                },
            });
            return { component, currentPageId };
        })
        .flatMap(({ component, currentPageId, select }) => {
            const actions = [];
            actions.push(mapSetOperation(currentPageId, component.get("id"), component, { buffer: false }));
            actions.push(componentActions.current.set(component.get("id")));
            return actions;
        });
};

export const mutateCurrentComponentEpic = (action$, store) => {
    return action$
        .ofType(WHITEBOARD_COMPONENT_MUTATE)
        .map(({ payload }) => {
            const { component, propsOrKey, value } = payload;
            const { mutate } = store.getState();
            const whiteboardToUpdate = payload.whiteboard ? payload.whiteboard : store.getState().whiteboard;
            const { currentPageId } = whiteboardToUpdate;
            const componentId = component.get("id");
            const componentToUpdate = mutate.snapshots.get(currentPageId).get(componentId) || component;
            if (typeof propsOrKey === "string") {
                return { component: componentToUpdate, currentPageId, key: propsOrKey, value };
            }
            return { component: componentToUpdate, currentPageId, props: propsOrKey, whiteboard: whiteboardToUpdate };
        })
        .flatMap(({ component, currentPageId, props, key, value, whiteboard }) => {
            const actions = [];
            if (!component) {
                actions.push(empty());
                return actions;
            }

            if (key) {
                actions.push(
                    mapSetOperation(currentPageId, component.get("id"), component.setIn(key.split("."), value), {
                        buffer: false,
                    }),
                );
                return actions;
            }

            if (props && props.product_image && props.product_image.url) {
                // We can't just use the product images stored on s3, but have to create a
                // board asset with that url
                const position = props.product_image.position || 1;
                actions.push(
                    productActions.changeProductImage({
                        url: props.product_image.url,
                        position,
                        component,
                        whiteboard,
                    }),
                );
                delete props.product_image;
            }

            actions.push(
                mapSetOperation(currentPageId, component.get("id"), component.mergeDeep(props), { buffer: false }),
            );

            return actions;
        });
};

export const removeCurrentComponentEpic = (action$, store) => {
    return action$
        .ofType(WHITEBOARD_COMPONENT_REMOVE)
        .map(({ payload }) => {
            const { whiteboard, mutate } = store.getState();
            const { currentPageId } = whiteboard;
            const page = mutate.snapshots.get(currentPageId);
            const nextSelectedComponent = page.find((value, key) => {
                return key !== payload;
            });
            let nextSelectedComponentId = null;
            if (nextSelectedComponent) {
                nextSelectedComponentId = nextSelectedComponent.get("id");
            }
            return { componentId: payload, currentPageId, nextSelectedComponentId };
        })
        .flatMap(({ componentId, currentPageId, nextSelectedComponentId }) => [
            mapRemoveOperation(currentPageId, componentId),
            {
                payload: nextSelectedComponentId,
                type: WHITEBOARD_COMPONENT_REMOVE_COMPLETED,
            },
        ]);
};

export const arrangeCurrentComponentEpic = (action$, store) => {
    return action$
        .ofType(WHITEBOARD_COMPONENT_ARRANGE)
        .map(({ payload }) => {
            // get data from store
            const { id, relativePosition } = payload;
            const { whiteboard, mutate } = store.getState();
            const { currentPageId } = whiteboard;
            const page = mutate.snapshots.get(currentPageId);
            const component = page.get(id);
            return { relativePosition, page, component, currentPageId };
        })
        .map(({ relativePosition, page, component, currentPageId }) => {
            // sort
            const currentZIndex = component.get("position").get("z_index");
            const orderedList = [];
            page.forEach((value, key) => orderedList.push({ key, value: value.get("position").get("z_index") }));
            orderedList.sort((a, b) => {
                if (a.value < b.value) {
                    return -1;
                } else if (a.value > b.value) {
                    return 1;
                }
                return 0;
            });
            if (orderedList.length <= 1) {
                return {};
            }
            let currentIndex;
            for (let i = 0; i < orderedList.length; i++) {
                if (orderedList[i].value === currentZIndex) {
                    currentIndex = i;
                    break;
                }
            }

            let zIndex = currentZIndex;
            let found = false;
            switch (relativePosition) {
                case Number.POSITIVE_INFINITY:
                    if (currentIndex !== orderedList.length - 1) {
                        zIndex = orderedList[orderedList.length - 1].value + 1;
                    }
                    break;
                case 1:
                    const overlayingComponents = getOverlayingComponents(page, component);
                    for (let i = currentIndex + 1; i < orderedList.length; i++) {
                        if (
                            overlayingComponents.includes(orderedList[i].key) ||
                            (!overlayingComponents.length && orderedList[i].value !== currentZIndex)
                        ) {
                            zIndex = orderedList[i].value + 1;
                            found = true;
                            break;
                        }
                    }
                    if (!found) {
                        zIndex = zIndex + 1;
                    }
                    break;
                case -1:
                    const underlyingComponents = getUnderlyingComponents(page, component);
                    for (let i = currentIndex - 1; i >= 0; i--) {
                        if (
                            underlyingComponents.includes(orderedList[i].key) ||
                            (!underlyingComponents.length && orderedList[i].value !== currentZIndex)
                        ) {
                            zIndex = orderedList[i].value - 1;
                            found = true;
                            break;
                        }
                    }
                    if (!found) {
                        zIndex = zIndex - 1;
                    }
                    break;
                case Number.NEGATIVE_INFINITY:
                    zIndex = orderedList[0].value - 1;
                    break;
                default:
            }
            return { currentPageId, component, zIndex };
        })
        .flatMap(({ currentPageId, component, zIndex }) => {
            if (zIndex === undefined) {
                return [];
            }
            return [
                mapSetOperation(
                    currentPageId,
                    component.get("id"),
                    component.mergeDeep({ position: { z_index: zIndex } }),
                    { buffer: false },
                ),
            ];
        });
};

const getModelForSchema = (schemaId, builder, schemas = {}) => {
    let Record = recordCache[schemaId];
    if (!Record) {
        // build Record
        const schema = schemas[schemaId];
        if (!schema) {
            // this should never happen
            throw new Error(`Schema ${schemaId} not found`);
        }

        Record = builder.create ? builder.create(schema) : builder(schema);
        recordCache[schemaId] = Record;
    }
    return Record;
};

const getRecordsFromSchema = (schema, records = {}) => {
    if (schema.record) {
        records[schema.key] = schema.record;
    }
    if (schema.schema) {
        if (schema.schema._key) {
            getRecordsFromSchema(schema.schema, records);
        } else {
            Object.keys(schema.schema).forEach((key) => {
                getRecordsFromSchema(schema.schema[key], records);
            });
        }
    }
    return records;
};

const normalizeData = (response, schemas, scope) => {
    const records = getRecordsFromSchema(ItemsSchema);
    const data = normalize(response.data || response, ItemsSchema);

    Object.keys(records).forEach((key) => {
        const recordOrBuilder = records[key];
        let Record = recordOrBuilder;
        const collection = data.entities[key];
        if (collection) {
            const keys = Object.keys(collection);
            if (keys.length) {
                // FDM
                if (!recordOrBuilder.prototype.toJS) {
                    if (!scope.brand && !scope.brand._id) {
                        return;
                    }
                    // check if not Immutable Record
                    const schemaId = collection[keys[0]].__schema_id;
                    Record = getModelForSchema(schemaId, recordOrBuilder, schemas);
                }
                keys.forEach((id) => {
                    collection[id] = new Record(collection[id]);
                });
            }
        }
    });

    return data;
};

const formatLabel = (item, key, productSchema) => {
    const details = productSchema.fields.get(key).get("details");

    let label = item.get(key);

    if (details.type === "multi") {
        label = label.join(", ");
    } else if (details.subtype === "date") {
        label = moment(label).format("MMM D, YYYY");
    }

    return label;
};

const getLabelTextFromItem = (item, key, scope, viewDef, schema) => {
    if (
        !item ||
        (viewDef && key === "mrsp" && !viewDef.sections.get("msrp")) ||
        (key === "price" && !viewDef.sections.get("pricing"))
    ) {
        return "";
    }
    switch (key) {
        case "msrp":
            return item.formattedMSRPRange(me._activeCurrency, scope);
        case "price":
            return item.formatPriceRange(me._activeCurrency, scope);
        case "__available_from":
        case "__available_until":
            return item.get(key) ? moment(item.get(key)).format("MMM D, YYYY") : "";
        default:
            return formatLabel(item, key, schema);
    }
};

const getItemTileViewDef = (views) => {
    let viewDef = {};

    Object.keys(views).forEach((view) => {
        if (views[view].view_name === VIEW_ITEM_TILE) {
            viewDef = views[view];
        }
    });
    return viewDef;
};

const getProductSchema = (schemas) => {
    let productSchema = {};
    Object.keys(schemas).forEach((schema) => {
        if (schemas[schema].type === "product") {
            productSchema = schemas[schema];
        }
    });
    return productSchema;
};

export const refreshLabelsEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_COMPONENTS_REFRESH)
        .map(({ payload }) => {
            const { ids, components } = payload;

            const query = {
                __archived: false,
                __fields: "__all",
                _id: ids,
                __meta: true,
                __populate: "__variant",
                __view_disabled: false,
            };

            return { query, components };
        })
        .switchMap(({ query, components }) =>
            ajax(`${siteConfig.api}/api/v3.0/items?${querystring.stringify(query)}`)
                .flatMap(({ response, status, xhr }) => {
                    const { scope, entities } = store.getState();
                    const data = normalizeData(response, entities.schemas, scope);
                    const viewDef = getItemTileViewDef(entities.views);
                    const productSchema = getProductSchema(entities.schemas);

                    const actions = [];

                    components.forEach((component) => {
                        const componentType = component.get("component");
                        let numberOfChangedLabels = 0;
                        const template = component.get(componentType).toJS();
                        const productId = template.product_1.product_id;
                        const product = data.entities.items[productId];
                        const labels = component.getIn([componentType, "labels"]).toJS();

                        labels.forEach((label) => {
                            if (label.product_source_field_name) {
                                const updatedText = getLabelTextFromItem(
                                    product,
                                    label.product_source_field_name,
                                    scope,
                                    viewDef,
                                    productSchema,
                                );
                                if (updatedText !== label.text) {
                                    label.text = updatedText;
                                    numberOfChangedLabels++;
                                }
                            }
                        });

                        if (numberOfChangedLabels) {
                            actions.push(
                                componentActions.mutate(component, {
                                    [componentType]: {
                                        labels,
                                    },
                                }),
                            );
                        }
                    });

                    actions.push(componentActions.refreshLabelsCompleted());

                    return actions;
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of(componentActions.refreshLabelsCompleted()),
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error fetching your products"),
                            }),
                        ),
                    ),
                ),
        );
